import { Button } from "@material-ui/core";
import { useAppInsights } from "Infrastructure/repositories/appInsights/AppInsights";
import {
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import { useUi } from "application/contexts/ui/UIContext";
import BottomModal from "components/bottomModal/BottomModal";
import { DiscountsService } from "domains/order/services/DiscountsService";
import { useTranslation } from "i18n/useTranslation";
import { GetCustomerDiscountsUseCase } from "modules/order/application/useCases/GetCustomerDiscountsUseCase";
import { IOrder } from "modules/order/domain/models/IOrder";
import React, {
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Trans } from "react-i18next";
import {
  ICreateDefferedPromise,
  createDefferedPromise,
} from "utils/DefferedPromise";
import { moneyMask } from "utils/utils";
import { ReactComponent as Discount } from "../../../../assets/icons/discount.svg";
import styles from "./CustomerDiscounts.module.scss";
import { applyDiscountsToOrder } from "./applyDiscounts";

export interface ICustomerDiscountsHandler {
  open(order: IOrder, eventId: string): Promise<IOrder>;
}

interface ICustomerDiscountsProps {}

const CustomerDiscounts: React.ForwardRefRenderFunction<
  ICustomerDiscountsHandler,
  ICustomerDiscountsProps
> = (_, ref) => {
  const deffered = useRef<ICreateDefferedPromise<IOrder>>(
    createDefferedPromise()
  );

  const { addLog } = useAppInsights();
  const { hideLoading, showLoading } = useUi();
  const { resolution } = useScreenStore();
  const { t } = useTranslation();

  const order = useRef<IOrder | null>(null);
  const newOrder = useRef<IOrder | null>(null);

  const [visible, setVisible] = useState(false);
  const [_order, setOrder] = useState<IOrder | null>(null);

  const handleClose = useCallback(() => {
    setVisible(false);
    deffered.current.rejecter("Fechado pelo usuário");
  }, []);

  const fetchCustomerDiscounts = useCallback(
    async (order: IOrder, eventId: string) => {
      showLoading();
      addLog("getCustomerDiscounts", {
        orderId: order.id,
        eventId: eventId,
      });

      const discountsService = DiscountsService();

      const discounts = await GetCustomerDiscountsUseCase(
        discountsService,
        order.customer?.document || "",
        eventId
      );

      addLog("getCustomerDiscounts_Result", {
        discounts: discounts,
      });

      const _newOrder = applyDiscountsToOrder(order, eventId, discounts ?? []);

      if (!_newOrder) {
        addLog("applyDiscountsToOrder_Result", {
          message: "No discounts applied",
        });
        deffered.current.resolver(order);
        setOrder(null);
        setVisible(false);
        hideLoading();
        return;
      }

      console.log("_applied discounts", _newOrder);

      setOrder(_newOrder);
      newOrder.current = _newOrder;
      hideLoading();
    },
    [addLog, hideLoading, showLoading]
  );

  const onConfirmOrder = useCallback(() => {
    if (newOrder.current) {
      setOrder(null);
      deffered.current.resolver(newOrder.current);
    } else {
      setOrder(null);
      deffered.current.resolver(order.current!);
    }
    setVisible(false);
  }, []);

  const open = useCallback(
    (_order: IOrder, eventId: string) => {
      deffered.current = createDefferedPromise<IOrder>();
      setOrder(null);
      order.current = _order;
      fetchCustomerDiscounts(_order, eventId);
      setVisible(true);

      return deffered.current.promise;
    },
    [fetchCustomerDiscounts]
  );

  const totalDiscounts = useMemo(
    () => _order?.orderItems.reduce((acc, it) => (acc += it?.discount ?? 0), 0),
    [_order]
  );

  useImperativeHandle(ref, () => ({ open }));

  return (
    <BottomModal showModal={visible} onClickClose={handleClose}>
      <div className={styles.container}>
        <h2>
          {_order?.customer?.name ? (
            <Trans i18nKey="customer_discounts.title">
              Descontos aplicados para
              <b>
                {{
                  name:
                    _order?.customer?.name?.toLowerCase() ??
                    t("customer_discounts.title_fallback"),
                }}
              </b>
            </Trans>
          ) : (
            <Trans i18nKey="customer_discounts.title_fallback">
              Descontos aplicados para <b>este pedido</b>
            </Trans>
          )}
        </h2>

        <div className={styles.discountItem}>
          {(_order?.orderItems || []).map((item, index) => (
            <div key={index}>
              {item?.discountQuantity && item?.discountQuantity > 0 ? (
                <Discount color="var(--primary)" />
              ) : null}
              <div>
                <span>{`${item.quantity} x `}</span>
                <span>{item.name}</span>
                {item.discountQuantity && item.discountQuantity > 0 ? (
                  <p>
                    <Trans i18nKey="customer_discounts.discount_applied_items">
                      Desconto aplicado a{" "}
                      <b>{{ count: item.discountQuantity }}</b> produto(s)
                    </Trans>
                  </p>
                ) : null}
              </div>
              <div>
                <span>
                  {item?.discountQuantity && item?.discountQuantity > 0
                    ? moneyMask(item.quantity * item.totalPrice)
                    : ""}
                </span>
                <span>
                  {moneyMask(
                    item.quantity * item.totalPrice - (item?.discount ?? 0)
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.total}>
          <div>
            <span>{t("customer_discounts.total_discount")}</span>
            <b>{moneyMask(totalDiscounts ?? 0)}</b>
          </div>
          <div>
            <span>{t("customer_discounts.total_to_pay")}</span>
            <span>
              {moneyMask((totalDiscounts ?? 0) > (_order?.totalValue ?? 0) ? 0 : _order?.totalValue ?? 0 )}
            </span>
          </div>
        </div>

        <Button
          className={styles.confirm}
          fullWidth
          variant="text"
          onClick={onConfirmOrder}
          style={{
            fontSize: resolution === ResolutionType.FULL_HD ? 45 : 32,
          }}
        >
          {t("customer_discounts.continue")}
        </Button>
      </div>
    </BottomModal>
  );
};
export default React.forwardRef(CustomerDiscounts);
